<template>
  <div>
    <div class="navbar">
      <div class="nav-left">
        <button class="btn btn-close" @click="goBack">Back</button>
      </div>
      <h2 class="title">Image Details</h2>
      <div class="nav-right">
        <button class="btn btn-clone" @click="cloneToNew" title="Create a copy of this metadata">
          <i class="fas fa-copy"></i> Clone
        </button>
        <button class="btn btn-edit" @click="toggleEdit">
          {{ isEditable ? 'Editing...' : 'Edit' }}
        </button>
      </div>
    </div>

    <div class="details-container card">
      <div class="character-image-details">
        
        <div v-if="mediaType === 'image'" class="media-wrapper">
          <img 
            :src="resolveUrl(character.path)" 
            alt="Image" 
            @load="onImageLoad" 
            @click="copyRichImage"
            class="clickable-image"
            title="Click to copy image"
          />
          <div class="copy-hint">Click image to copy</div>
        </div>
        <div v-else-if="mediaType === 'video'" class="media-wrapper">
          <video controls :src="resolveUrl(character.path)" style="width: 100%; border-radius: 8px;"></video>
        </div>
        <div v-else-if="mediaType === 'audio'" class="media-wrapper audio-wrapper">
          <i class="fas fa-music audio-icon"></i>
          <audio controls :src="resolveUrl(character.path)" style="width: 100%;"></audio>
        </div>
        <div v-else-if="mediaType === 'embed'" class="media-wrapper">
          <iframe :src="embedUrl" width="100%" height="200" frameborder="0" allowfullscreen style="border-radius: 8px;"></iframe>
        </div>

        <button v-if="!isEditable && mediaType === 'image'" class="btn btn-adjust-size" @click="openSizeModal">
          <i class="fas fa-expand-arrows-alt"></i> Size & Style
        </button>
        
        <button v-if="!isEditable && mediaType !== 'image'" class="btn btn-adjust-size" @click="copyHtmlCode">
          <i class="fas fa-code"></i> Copy Embed Code
        </button>
      </div>

      <div class="character-info">
        <div v-if="!isEditable" class="meta-info-row">
          <div class="meta-item" v-if="character.source"><span class="label">Source:</span> {{ character.source }}</div>
          <div class="meta-item" v-if="character.added_by"><span class="label">Added by:</span> {{ character.added_by }}</div>
          <div class="meta-item" v-if="character.art_style"><span class="label">Style:</span> {{ character.art_style }}</div>
          <div class="meta-item ai-badge" v-if="character.is_ai"><i class="fas fa-robot"></i> AI Generated</div>
        </div>
        
        <div v-if="!isEditable && character.details" class="details-section">
          <label>Details / Prompt:</label>
          <p>{{ character.details }}</p>
        </div>

        <div class="form-group"><label>Name:</label><input v-model="character.name" :disabled="!isEditable" /></div>
        <div class="form-group"><label>ALT TEXT (Title):</label><input v-model="character.title" :disabled="!isEditable" @input="generateHtmlCode" /></div>
        <div class="form-group"><label>Path / URL / Embed Code:</label><input v-model="character.path" :disabled="!isEditable" @input="onPathChange" /></div>

        <div v-if="isEditable" class="form-row">
          <div class="form-group half-width"><label>Source (Mandatory):</label><input v-model="character.source" :class="{'error-border': attemptSave && !character.source}" /><span v-if="attemptSave && !character.source" class="error-text">Source is required</span></div>
          <div class="form-group half-width"><label>Added By (Optional):</label><input v-model="character.added_by" /></div>
        </div>

        <div v-if="isEditable">
          <div class="form-group"><label>Details (Optional):</label><textarea v-model="character.details" rows="3"></textarea></div>
          <div class="form-group"><label>Art Style (Optional):</label><input v-model="character.art_style" /></div>
          <div class="form-group checkbox-group"><label class="checkbox-label"><input type="checkbox" v-model="character.is_ai" /><span class="label-text"><i class="fas fa-robot"></i> AI Generated Image</span></label></div>
        </div>

        <div v-if="isEditable && mediaType === 'image'" class="form-group">
          <label>Preferred Image Size</label>
          <div class="size-controls">
            <div class="size-input-group"><label class="small-label">Width:</label><input type="number" v-model.number="imageSize.width" placeholder="Auto" min="1" @input="onWidthChange" /><span class="unit">px</span></div>
            <button type="button" class="btn-lock" @click="toggleLock"><i :class="isLocked ? 'fas fa-lock' : 'fas fa-lock-open'"></i></button>
            <div class="size-input-group"><label class="small-label">Height:</label><input type="number" v-model.number="imageSize.height" placeholder="Auto" min="1" @input="onHeightChange" /><span class="unit">px</span></div>
          </div>
        </div>

        <div class="form-group">
          <label>HTML / Embed Code:</label>
          <div class="html-code-container">
            <textarea v-model="character.htmlCode" readonly></textarea>
            <button class="btn btn-copy-html" @click="copyHtmlCode"><i class="fas fa-copy"></i> Copy Code</button>
          </div>
          <div class="copy-overlay-details" v-if="copied">Copied!</div>
        </div>

        <div class="form-group"><label>Styles:</label><input v-model="character.styles" :disabled="!isEditable" /></div>
        
        <div class="form-group">
          <label>Tags:</label>
          <input v-model="tagsInput" :disabled="!isEditable" placeholder="Comma separated tags" list="all-tags" />
          <datalist id="all-tags"><option v-for="tag in allUniqueTags" :key="tag" :value="tag" /></datalist>
          <div v-if="isEditable && commonTags.length > 0" class="tag-suggestions">
            <span class="suggestion-label">Common tags:</span>
            <div class="suggestion-list"><button v-for="tag in commonTags" :key="tag" class="tag-btn" @click="addTag(tag)" type="button">+ {{ tag }}</button></div>
          </div>
        </div>

        <div class="action-buttons" v-if="isEditable">
          <button class="btn btn-delete" @click="confirmDelete">Delete</button>
          <div class="right-actions">
            <button class="btn btn-cancel" @click="cancelEdit">Cancel</button>
            <button class="btn btn-save" @click="saveDetails">Save</button>
          </div>
        </div>
      </div>
    </div>
    
    <div v-if="showDeleteConfirmation" class="delete-confirmation-overlay">
      <div class="delete-confirmation-box">
        <p>Delete this item?</p>
        <button class="btn btn-yes" @click="deleteCharacter">Yes</button>
        <button class="btn btn-no" @click="closeDeleteConfirmation">No</button>
      </div>
    </div>

    <ImageSizeModal v-if="showSizeModal" :visible="showSizeModal" :character="character" @close="closeSizeModal" @copied="handleCopied" />
  </div>
</template>

<script>
import api from '../services/api';
import ImageSizeModal from './ImageSizeModal.vue';

export default {
  components: { ImageSizeModal },
  data() {
    return {
      character: { name: '', title: '', path: '', htmlCode: '', tags: [], styles: '', preferredWidth: null, preferredHeight: null, source: '', added_by: '', details: '', is_ai: false, art_style: '' },
      imageSize: { width: null, height: null },
      originalCharacter: null,
      isEditable: false, isLocked: true, aspectRatio: null, tagsInput: '', copied: false,
      showDeleteConfirmation: false, showSizeModal: false, attemptSave: false,
      existingCharacters: []
    };
  },
  computed: {
    allUniqueTags() {
      const tags = new Set();
      this.existingCharacters.forEach(c => { if (c.tags && Array.isArray(c.tags)) c.tags.forEach(t => tags.add(t)); });
      return Array.from(tags).sort();
    },
    commonTags() {
      const counts = {};
      this.existingCharacters.forEach(c => { if (c.tags && Array.isArray(c.tags)) c.tags.forEach(t => { counts[t] = (counts[t] || 0) + 1; }); });
      return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 12).map(e => e[0]);
    },
    mediaType() {
      const path = this.character.path ? this.character.path.toLowerCase().split('?')[0] : '';
      if (path.includes('youtube.com') || path.includes('youtu.be') || path.includes('vimeo.com')) return 'embed';
      if (path.endsWith('.mp4') || path.endsWith('.webm') || path.endsWith('.ogg') || path.endsWith('.mov')) return 'video';
      if (path.endsWith('.mp3') || path.endsWith('.wav')) return 'audio';
      return 'image';
    },
    embedUrl() {
      let url = this.character.path;
      if (url.includes('youtube.com/watch?v=')) return url.replace('watch?v=', 'embed/');
      if (url.includes('youtu.be/')) return url.replace('youtu.be/', 'www.youtube.com/embed/');
      return url;
    }
  },
  methods: {
    cloneToNew() {
      sessionStorage.setItem('cloneData', JSON.stringify(this.character));
      this.$router.push({ name: 'AddCharacter' });
    },
    resolveUrl(path) {
      if (!path) return '';
      if (path.startsWith('/shared/online/')) return 'https://lms.unb.ca' + path;
      return path;
    },
    async fetchCharacterDetails() {
      try {
        this.character = await api.getCharacter(this.$route.params.id);
        this.tagsInput = Array.isArray(this.character.tags) ? this.character.tags.join(', ') : '';
        this.imageSize.width = this.character.preferredWidth;
        this.imageSize.height = this.character.preferredHeight;
        if (!this.character.added_by) this.character.added_by = '';
        if (!this.character.source) this.character.source = '';
        if (!this.character.art_style) this.character.art_style = '';
        this.originalCharacter = JSON.parse(JSON.stringify(this.character));
        this.existingCharacters = await api.getAllCharacters();
      } catch (error) { console.error(error); alert('Failed to load.'); }
    },
    addTag(tag) { const current = this.tagsInput.split(',').map(t => t.trim()).filter(t => t); if (!current.includes(tag)) { current.push(tag); this.tagsInput = current.join(', '); } },
    onPathChange() {
      const raw = this.character.path;
      const srcMatch = raw.match(/src\s*=\s*["']([^"']+)["']/i);
      if (srcMatch && srcMatch[1]) this.character.path = srcMatch[1];
      this.generateHtmlCode();
    },
    onImageLoad(event) { this.aspectRatio = event.target.naturalWidth / event.target.naturalHeight; },
    toggleLock() { this.isLocked = !this.isLocked; },
    onWidthChange() { if (this.isLocked && this.aspectRatio) this.imageSize.height = Math.round(this.imageSize.width / this.aspectRatio); this.generateHtmlCode(); },
    onHeightChange() { if (this.isLocked && this.aspectRatio) this.imageSize.width = Math.round(this.imageSize.height * this.aspectRatio); this.generateHtmlCode(); },
    generateHtmlCode() {
      const type = this.mediaType;
      const fullPath = this.resolveUrl(this.character.path);
      const title = this.character.title || this.character.name || 'Image';
      if (!fullPath) { this.character.htmlCode = ''; return; }
      if (type === 'video') this.character.htmlCode = `<p><video src="${fullPath}" controls style="max-width: 100%;"></video></p>`;
      else if (type === 'audio') this.character.htmlCode = `<p><audio src="${fullPath}" controls></audio></p>`;
      else if (type === 'embed') this.character.htmlCode = `<p><iframe src="${this.embedUrl}" width="560" height="315" frameborder="0" allowfullscreen></iframe></p>`;
      else {
        let styleAttr = 'max-width: 100%;';
        if (this.imageSize.width || this.imageSize.height) {
          const w = this.imageSize.width ? `width: ${this.imageSize.width}px;` : '';
          const h = this.imageSize.height ? `height: ${this.imageSize.height}px;` : '';
          styleAttr = `${w} ${h} max-width: 100%;`.trim();
        }
        this.character.htmlCode = `<p><img src="${fullPath}" alt="${title}" title="${title}" data-d2l-editor-default-img-style="true" style="${styleAttr}"></p>`;
      }
    },
    async copyRichImage() {
      if (this.mediaType !== 'image') { this.copyHtmlCode(); return; }
      const html = this.character.htmlCode;
      try {
        const blobHtml = new Blob([html], { type: "text/html" });
        const blobText = new Blob([html], { type: "text/plain" });
        await navigator.clipboard.write([new ClipboardItem({ "text/html": blobHtml, "text/plain": blobText })]);
        this.handleCopied();
      } catch (err) { this.copyHtmlCode(); }
    },
    toggleEdit() {
      this.isEditable = !this.isEditable;
      if (this.isEditable) this.originalCharacter = JSON.parse(JSON.stringify(this.character));
      else this.cancelEdit();
    },
    cancelEdit() {
      this.isEditable = false;
      this.character = JSON.parse(JSON.stringify(this.originalCharacter));
      this.tagsInput = this.character.tags.join(', ');
      this.imageSize.width = this.character.preferredWidth;
      this.imageSize.height = this.character.preferredHeight;
    },
    async saveDetails() {
      this.attemptSave = true;
      if (!this.character.source) { alert("Source is required"); return; }
      this.character.tags = this.tagsInput.split(',').map(tag => tag.trim()).filter(t => t);
      this.character.preferredWidth = this.imageSize.width;
      this.character.preferredHeight = this.imageSize.height;
      this.character.addedBy = this.character.added_by;
      this.character.isAi = this.character.is_ai ? 1 : 0;
      this.character.artStyle = this.character.art_style;
      try {
        await api.updateCharacter(this.character.id, this.character);
        this.isEditable = false;
        this.attemptSave = false;
        this.originalCharacter = JSON.parse(JSON.stringify(this.character));
      } catch (error) { console.error(error); alert('Failed to save.'); }
    },
    copyHtmlCode() { navigator.clipboard.writeText(this.character.htmlCode).then(this.handleCopied); },
    openSizeModal() { this.showSizeModal = true; },
    closeSizeModal() { this.showSizeModal = false; },
    handleCopied() { this.copied = true; setTimeout(() => this.copied = false, 2000); },
    goBack() { this.$router.push({ name: 'CharacterList' }); },
    confirmDelete() { this.showDeleteConfirmation = true; },
    closeDeleteConfirmation() { this.showDeleteConfirmation = false; },
    async deleteCharacter() { await api.deleteCharacter(this.character.id); this.$router.push({ name: 'CharacterList' }); },
  },
  mounted() { this.fetchCharacterDetails(); },
};
</script>

<style scoped>
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css');

.navbar { display: flex; justify-content: space-between; align-items: center; background: var(--primary-color); padding: 15px 20px; color: white; }
.nav-right { display: flex; gap: 10px; }
.btn-edit { background: var(--accent-color); color: white; padding: 8px 16px; border-radius: 4px; border: none; cursor: pointer; }
.btn-clone { background: #3498db; color: white; padding: 8px 16px; border-radius: 4px; border: none; cursor: pointer; }
.btn-close { background: var(--danger-color); color: white; padding: 8px 16px; border-radius: 4px; border: none; cursor: pointer; }
.details-container { max-width: 800px; margin: 40px auto; display: flex; gap: 40px; padding: 20px; background: white; border: 2px solid #000; border-bottom: 4px solid #000; box-shadow: 0 12px 24px rgba(0,0,0,0.2); border-radius: 8px; }
.character-image-details { max-width: 300px; width: 100%; display: flex; flex-direction: column; }
.media-wrapper { width: 100%; margin-bottom: 10px; position: relative; }
.clickable-image { cursor: copy; transition: opacity 0.2s; }
.clickable-image:hover { opacity: 0.9; }
.copy-hint { position: absolute; bottom: 10px; left: 50%; transform: translateX(-50%); background: rgba(0,0,0,0.7); color: white; padding: 4px 8px; border-radius: 4px; font-size: 12px; pointer-events: none; opacity: 0; transition: opacity 0.2s; }
.media-wrapper:hover .copy-hint { opacity: 1; }
.character-image-details img { width: 100%; border: 2px solid #000; border-radius: 8px; }
.btn-adjust-size { width: 100%; margin-top: 10px; background: var(--accent-color); color: white; padding: 10px; border: none; border-radius: 4px; cursor: pointer; }
.audio-wrapper { display: flex; flex-direction: column; align-items: center; padding: 20px; background: #f0f0f0; border-radius: 8px; border: 2px solid #000; }
.audio-icon { font-size: 40px; margin-bottom: 10px; color: #666; }
.character-info { flex: 1; display: flex; flex-direction: column; gap: 15px; }
.form-group { display: flex; flex-direction: column; }
.form-group label { margin-bottom: 5px; font-weight: bold; }
.form-group input, .form-group textarea { padding: 10px; border: 1px solid #000; background: rgba(246,219,53); border-radius: 4px; }
.form-group input:disabled, .form-group textarea:disabled { background: #eee; cursor: default; }
.meta-info-row { display: flex; gap: 20px; padding: 10px; background: #f0f0f0; border-radius: 4px; margin-bottom: 10px; flex-wrap: wrap; }
.meta-item { font-size: 14px; }
.meta-item .label { font-weight: bold; }
.ai-badge { color: #5e35b1; font-weight: bold; display: flex; align-items: center; gap: 5px; }
.details-section { background: #fff8e1; padding: 15px; border-radius: 4px; border-left: 4px solid #ffc107; margin-bottom: 10px; }
.details-section label { font-weight: bold; font-size: 12px; text-transform: uppercase; color: #f57f17; }
.details-section p { margin: 5px 0 0; font-size: 14px; white-space: pre-wrap; }
.form-row { display: flex; gap: 20px; }
.half-width { flex: 1; }
.error-border { border: 2px solid red !important; }
.error-text { color: red; font-size: 12px; }
.html-code-container { display: flex; flex-direction: column; }
.btn-copy-html { margin-top: 5px; background: var(--accent-color); color: white; border: none; padding: 5px; cursor: pointer; border-radius: 4px; }
.action-buttons { display: flex; justify-content: space-between; margin-top: 20px; }
.right-actions { display: flex; gap: 10px; }
.btn-save { background: var(--accent-color); color: white; padding: 10px 20px; border-radius: 4px; border: none; cursor: pointer; }
.btn-cancel { background: #888; color: white; padding: 10px 20px; border-radius: 4px; border: none; cursor: pointer; }
.btn-delete { background: var(--danger-color); color: white; padding: 10px 20px; border-radius: 4px; border: none; cursor: pointer; }
.tag-suggestions { margin-top: 10px; padding: 10px; background: #f9f9f9; border-radius: 4px; border: 1px solid #eee; }
.suggestion-label { display: block; font-size: 12px; color: #666; margin-bottom: 5px; font-weight: bold; }
.suggestion-list { display: flex; flex-wrap: wrap; gap: 5px; }
.tag-btn { background: #e0f7fa; border: 1px solid #b2ebf2; padding: 4px 8px; border-radius: 12px; font-size: 12px; cursor: pointer; color: #006064; transition: background 0.2s; }
.tag-btn:hover { background: #b2ebf2; }
.copy-overlay-details { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); background: rgba(0,0,0,0.8); color: white; padding: 10px; border-radius: 4px; }
.delete-confirmation-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); display: flex; justify-content: center; align-items: center; z-index: 999; }
.delete-confirmation-box { background: white; padding: 20px; border-radius: 8px; text-align: center; }
.btn-yes { background: var(--danger-color); color: white; margin-right: 10px; padding: 8px 16px; border: none; border-radius: 4px; cursor: pointer; }
.btn-no { background: #ccc; padding: 8px 16px; border: none; border-radius: 4px; cursor: pointer; }
.size-controls { display: flex; gap: 10px; align-items: center; }
.size-input-group { display: flex; align-items: center; gap: 5px; }
.size-input-group input { width: 70px; }
.btn-lock { background: none; border: 1px solid #000; border-radius: 50%; width: 30px; height: 30px; cursor: pointer; }
.checkbox-group { margin-top: 10px; }
.checkbox-label { display: flex; align-items: center; gap: 10px; cursor: pointer; font-weight: bold; }
.label-text { display: flex; align-items: center; gap: 5px; }

@media (max-width: 768px) {
  .details-container { flex-direction: column; }
  .character-image-details { max-width: 100%; }
}
</style>